import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  compressImageForUpload,
  detectUploadMime,
  MAX_UPLOAD_BYTES,
  type UploadImageMime,
} from "../util/imageCompression.js";
import { ApiError, opsFetch } from "./client.js";

export type RfqStatus = "SUBMITTED" | "IN_REVIEW" | "CLOSED" | "CANCELLED";

/** Editable request fields — mirrors customer/api/src/domain/rfq.ts. */
export type ServiceCategory =
  | "plumbing"
  | "electrical"
  | "hvac"
  | "roofing"
  | "landscaping"
  | "painting"
  | "cleaning"
  | "appliance_repair"
  | "general_handyman"
  | "pest_control"
  | "other";

export type Urgency = "emergency" | "urgent" | "normal" | "flexible";

/** Values must match serviceCategoryEnum exactly; labels are staff-facing only. */
export const SERVICE_CATEGORIES: { value: ServiceCategory; label: string }[] = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
  { value: "roofing", label: "Roofing" },
  { value: "landscaping", label: "Landscaping" },
  { value: "painting", label: "Painting" },
  { value: "cleaning", label: "Cleaning" },
  { value: "appliance_repair", label: "Appliance repair" },
  { value: "general_handyman", label: "General handyman" },
  { value: "pest_control", label: "Pest control" },
  { value: "other", label: "Other" },
];

/** Values must match urgencyEnum exactly. */
export const URGENCIES: { value: Urgency; label: string }[] = [
  { value: "emergency", label: "Emergency · within 24h" },
  { value: "urgent", label: "Urgent · within a few days" },
  { value: "normal", label: "Normal · within ~2 weeks" },
  { value: "flexible", label: "Flexible · whenever" },
];

/** Must match updateRfqSchema's description cap in the customer system. */
export const MAX_DESCRIPTION_LENGTH = 2000;

/**
 * The job site, mirroring `RfqLocation` in customer/api/src/domain/rfq.ts.
 *
 * The street and unit have always been on the wire — this type declared only
 * city/state/postalCode and silently dropped the rest, which left staff with
 * an address they could match on but not dispatch to.
 */
export interface RfqLocation {
  street: string;
  unit?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
}

export interface RfqListItem {
  id: string;
  customerSub: string;
  status: RfqStatus;
  serviceCategory: string;
  urgency: string;
  description: string;
  location: RfqLocation;
  createdAt: string;
  /** Present on the list too — the queue uses them for at-a-glance triage. */
  images?: RfqImage[];
  adminNotes?: AdminNote[];
}

export interface RfqImage {
  id: string;
  key: string;
  /** Presigned GET, added by the customer system when it assembles the detail. */
  url?: string;
  contentType?: UploadImageMime;
  sizeBytes?: number;
  uploadedAt?: string;
}
export interface AdminNote {
  id: string;
  authorSub: string;
  text: string;
  createdAt: string;
}
export interface RfqQuestion {
  questionId: string;
  question: string;
  answer?: string;
  askedAt: string;
  answeredAt?: string;
}

/**
 * Mirrors `CustomerProfile` in customer/api/src/lib/cognito.ts. The phone
 * field is `phoneNumber` there; this type called it `phone`, so every request
 * rendered a dash where the callback number should have been.
 */
export interface CustomerContact {
  givenName?: string;
  familyName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface QuoteProjection {
  quoteId: string;
  status: "ACTIVE" | "WITHDRAWN";
  quote?: {
    type: string;
    amount?: { amountCents: number };
    minAmount?: { amountCents: number };
    maxAmount?: { amountCents: number };
    notes?: string;
    /** When the pro says they can actually come — already projected, never shown. */
    availabilityWindows?: { start: string; end: string }[];
  };
  contractorProfile?: { businessName: string; yearsExperience: number };
  updatedAt: string;
}
/** The pro's rating OF the customer. Staff-only — never shown to either party. */
export interface ContractorFeedback {
  rating: number;
  wouldWorkAgain: boolean;
  comment?: string;
  submittedAt: string;
}

export interface JobProjection {
  jobId: string;
  status: string;
  scheduledFor?: string;
  statusTimestamps: Record<string, string>;
  contractorFeedback?: ContractorFeedback;
}

/** The customer's rating of the pro. Staff-only. */
export interface CustomerFeedback {
  rfqId: string;
  quoteId: string;
  workCompleted: boolean;
  rating?: number;
  comment?: string;
  source: "customer" | "staff";
  submittedAt: string;
}

export interface RfqDetail {
  rfq: RfqListItem & {
    images: RfqImage[];
    questions?: RfqQuestion[];
    adminNotes?: AdminNote[];
    updatedAt: string;
  };
  customer?: CustomerContact;
  quotes: QuoteProjection[];
  selection: {
    quoteId: string;
    status: "SELECTED" | "VOIDED";
    /** Set only when staff accepted for the customer; absent if they chose. */
    selectedBy?: string;
    selectedAt?: string;
  } | null;
  jobs: JobProjection[];
  feedback: CustomerFeedback | null;
}

export function useRfqs(status: string) {
  return useQuery({
    queryKey: ["ops", "rfqs", status],
    queryFn: () =>
      opsFetch<{ items: RfqListItem[] }>(`/ops/rfqs?status=${encodeURIComponent(status)}`),
  });
}

export function useRfq(rfqId: string | undefined) {
  return useQuery({
    queryKey: ["ops", "rfq", rfqId],
    enabled: Boolean(rfqId),
    queryFn: () => opsFetch<RfqDetail>(`/ops/rfqs/${rfqId!}`),
  });
}

/**
 * Move the request's status. An optional note rides along and is recorded as
 * a triage note server-side — cancelling and reopening are the transitions
 * whose reason someone will want later, and the status alone doesn't carry it.
 */
export function useRfqStatus(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { status: "IN_REVIEW" | "CLOSED" | "CANCELLED"; note?: string }) =>
      opsFetch(`/ops/rfqs/${rfqId}/status`, {
        method: "POST",
        json: { status: input.status, ...(input.note ? { note: input.note } : {}) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
      void qc.invalidateQueries({ queryKey: ["ops", "rfqs"] });
    },
  });
}

/**
 * Accept a quote on the customer's behalf (admin only, server-enforced). The
 * customer system treats this exactly like the customer pressing "Choose this
 * quote": contact details go to the winner and the losing invitations are
 * revoked. `reason` is recorded as a triage note — for an action taken for
 * someone else, why is worth as much as what.
 *
 * Invalidates the jobs roll-up too: selection is what creates the job.
 */
export function useRfqSelectQuote(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { quoteId: string; reason?: string }) =>
      opsFetch(`/ops/rfqs/${rfqId}/quotes/${input.quoteId}/select`, {
        method: "POST",
        json: input.reason ? { note: input.reason } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
      void qc.invalidateQueries({ queryKey: ["ops", "rfqs"] });
    },
  });
}

/** A bad outcome waiting on a human: work not done, or 1-2 stars. */
export interface FlaggedFeedback {
  rfqId: string;
  workCompleted: boolean;
  rating?: number;
  comment?: string;
  submittedAt: string;
  source: "customer" | "staff";
  description?: string;
  serviceCategory?: ServiceCategory;
  rfqStatus?: RfqStatus;
  businessName?: string;
}

/**
 * The follow-up queue, oldest first. Polled rather than fetched once: a
 * staffer leaves this screen open, and a new bad outcome arriving should show
 * up without them thinking to reload.
 */
export function useFlaggedFeedback() {
  return useQuery({
    queryKey: ["ops", "feedback", "flagged"],
    queryFn: () => opsFetch<{ items: FlaggedFeedback[] }>("/ops/feedback/flagged"),
    refetchInterval: 60_000,
  });
}

/** Clear one from the queue once it has been dealt with. */
export function useResolveFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { rfqId: string; seenAt: string; note?: string }) =>
      opsFetch(`/ops/feedback/${input.rfqId}/resolve`, {
        method: "POST",
        // seenAt pins the write to the complaint actually on screen; a 409
        // means it changed underneath and the list needs re-reading.
        json: { seenAt: input.seenAt, ...(input.note ? { note: input.note } : {}) },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops", "feedback", "flagged"] }),
    // A conflict also means the list is stale — pull it fresh so the staffer
    // is looking at the truth while they read the message.
    onError: () => void qc.invalidateQueries({ queryKey: ["ops", "feedback", "flagged"] }),
  });
}

export function useRfqNote(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      opsFetch(`/ops/rfqs/${rfqId}/notes`, { method: "POST", json: { text } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] }),
  });
}

// ---- Manual contractor invite (admin) --------------------------------------

export interface InviteContractorInput {
  /**
   * Exactly one identifier is required. `contractorId` is what the registry
   * picker sends — an existing record, chosen from a list. `email` is the
   * hand-typed path, and the only one that can create a new contractor.
   */
  contractorId?: string;
  email?: string;
  /** Required by the server only when the email isn't in the registry yet. */
  businessName?: string;
  contactName?: string;
  phone?: string;
  /**
   * Why this pro, in the staffer's words. Reaches the contractor in their
   * invitation email — the difference between a hand-pick and what otherwise
   * looks like a matching bug to a roofer sent a handyman job.
   */
  note?: string;
}

export interface InviteContractorResult {
  contractor: { contractorId: string; businessName: string; status: string; created: boolean };
  invitation: {
    invitationId: string;
    /** The magic link, shown once for copy/share. Never logged. */
    link: string;
    expiresAt: string;
    /** false = this pro already had an invitation; the link was re-minted. */
    created: boolean;
    emailSent: boolean;
    emailSuppressed: boolean;
  };
}

/**
 * Mint a magic link for a contractor (by registry record or business email)
 * on this request and email it to them. Re-inviting the same pro replaces
 * their previous link. The request's service category is not consulted: an
 * admin may invite anyone, which is the point of inviting by hand.
 */
export function useRfqInvite(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteContractorInput) =>
      opsFetch<InviteContractorResult>(`/ops/rfqs/${rfqId}/invitations`, {
        method: "POST",
        json: input,
      }),
    onSuccess: () => {
      // The invitations list hangs off the same key prefix, so one
      // invalidation refreshes both the record and the "who's been asked" card.
      void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
      // An invite by email can CREATE a registry record, so the contractor
      // lists and any open search are stale the moment this succeeds.
      void qc.invalidateQueries({ queryKey: ["ops", "contractors"] });
    },
  });
}

// ---- Manual matching (admin|support reads) --------------------------------

/** An approved pro who covers this job's category and ZIP. */
export interface SuggestedPro {
  contractorId: string;
  businessName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  status: string;
  emailSuppressed: boolean;
  serviceCategories: string[];
  serviceArea: { postalCodes: string[]; cities: string[] };
  yearsExperience?: number;
}

export interface RfqInvitationSummary {
  invitationId: string;
  contractorId: string;
  businessName: string;
  email?: string;
  phone?: string;
  emailSuppressed: boolean;
  status: string;
  expired: boolean;
  notifiedAt?: string;
  tokenExpiresAt: string;
  revokedAt?: string;
  pendingQuestion?: string;
  createdAt: string;
}

/**
 * Approved pros covering this request. The server runs the same query the
 * automatic fan-out uses, so what a staffer sees here is what the system
 * would have picked on its own.
 */
export function useRfqMatches(
  rfqId: string | undefined,
  category: string | undefined,
  postalCode: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["ops", "rfq", rfqId, "matches", category, postalCode],
    enabled: Boolean(rfqId && category && postalCode && enabled),
    queryFn: () =>
      opsFetch<{ items: SuggestedPro[] }>(
        `/ops/rfqs/${rfqId!}/matches?category=${encodeURIComponent(category!)}&postalCode=${encodeURIComponent(postalCode!)}`,
      ),
  });
}

/** Who has been invited on this request, and whether they responded. */
export function useRfqInvitations(rfqId: string | undefined) {
  return useQuery({
    queryKey: ["ops", "rfq", rfqId, "invitations"],
    enabled: Boolean(rfqId),
    queryFn: () => opsFetch<{ items: RfqInvitationSummary[] }>(`/ops/rfqs/${rfqId!}/invitations`),
  });
}

// ---- Staff editing (admin|support, SUBMITTED|IN_REVIEW) --------------------

export interface UpdateRfqInput {
  serviceCategory?: ServiceCategory;
  urgency?: Urgency;
  description?: string;
}

/**
 * Surface what the BFF actually said. Staff act on these messages, and the one
 * that matters is 409: someone else moved the request on while this page sat
 * open, so the only useful advice is "reload and look again".
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // The customer system answers in two envelopes and both reach us verbatim
    // through the BFF: hand-written 404/409s carry `message`, while anything
    // rejected by zod in withErrorHandling carries `error`. Read both, or a
    // validation failure surfaces as a bare "API 400 Bad Request".
    const body = err.body as { message?: string; error?: string } | null;
    const message = body?.message ?? body?.error;
    if (err.status === 409) {
      return `${message ?? "This request has already moved on."} Reload the page to see its current state.`;
    }
    return message ?? err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

/** PATCH the editable fields. Send only what changed — the server takes a partial. */
export function useRfqUpdate(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRfqInput) =>
      opsFetch(`/ops/rfqs/${rfqId}`, { method: "PATCH", json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
      void qc.invalidateQueries({ queryKey: ["ops", "rfqs"] });
    },
  });
}

export function useRfqImageDelete(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) =>
      opsFetch(`/ops/rfqs/${rfqId}/images/${imageId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
      void qc.invalidateQueries({ queryKey: ["ops", "rfqs"] });
    },
  });
}

interface PresignUploadResponse {
  imageId: string;
  key: string;
  contentType: UploadImageMime;
  uploadUrl: string;
  expiresInSeconds: number;
}

/** One file's place in a batch, so a single bad photo reads as one bad photo. */
export interface RfqImageUpload {
  id: string;
  name: string;
  status: "uploading" | "done" | "error";
  /** 0–1, from the S3 PUT. */
  progress: number;
  error?: string;
}

/**
 * Presign → PUT → attach for one file. Compression runs first because it can
 * change the content type (HEIC → JPEG) and the server binds Content-Type into
 * the presigned PUT, so we have to presign for what we will actually send.
 */
async function uploadOne(
  rfqId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  const detected = detectUploadMime(file);
  if (!detected) throw new Error("Unsupported image type — use JPG, PNG, WebP, or HEIC.");
  if (file.size > MAX_UPLOAD_BYTES) throw new Error("Too large (max 25 MB).");

  const compressed = await compressImageForUpload(file, detected);
  const presign = await opsFetch<PresignUploadResponse>(`/ops/rfqs/${rfqId}/images/presign`, {
    method: "POST",
    json: { contentType: compressed.contentType },
  });

  // XHR rather than fetch: only XHR reports request-body progress. The PUT goes
  // straight to S3 with no Authorization header — the signature is in the URL.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.uploadUrl);
    // From the presign response, not our local copy — the header must match the
    // signed one exactly or S3 rejects the upload.
    xhr.setRequestHeader("Content-Type", presign.contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && ev.total > 0) onProgress(ev.loaded / ev.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (HTTP ${xhr.status}).`));
    xhr.onerror = () => reject(new Error("Upload failed — network error."));
    xhr.send(compressed.file);
  });

  await opsFetch(`/ops/rfqs/${rfqId}/images`, {
    method: "POST",
    json: {
      imageId: presign.imageId,
      key: presign.key,
      contentType: presign.contentType,
      sizeBytes: compressed.file.size,
    },
  });
}

/**
 * Batch photo upload with per-file state. Files are processed one at a time:
 * attach read-modify-writes the same RFQ record, so a parallel batch would have
 * the writes clobber each other's image lists. A file that fails is recorded
 * against that file and the batch carries on.
 */
export function useRfqImageUpload(rfqId: string) {
  const qc = useQueryClient();
  const [uploads, setUploads] = useState<RfqImageUpload[]>([]);
  const [isPending, setIsPending] = useState(false);

  const upload = useCallback(
    async (files: FileList | File[] | null) => {
      const batch = files ? Array.from(files) : [];
      if (batch.length === 0) return;

      const stamp = Date.now();
      const jobs = batch.map((file, i) => ({ file, id: `${stamp}-${i}-${file.name}` }));
      setUploads((prev) => [
        ...prev,
        ...jobs.map(({ file, id }) => ({
          id,
          name: file.name,
          status: "uploading" as const,
          progress: 0,
        })),
      ]);
      setIsPending(true);

      const patch = (id: string, next: Partial<RfqImageUpload>) =>
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...next } : u)));

      let attached = 0;
      for (const { file, id } of jobs) {
        try {
          await uploadOne(rfqId, file, (fraction) => patch(id, { progress: fraction }));
          patch(id, { status: "done", progress: 1 });
          attached += 1;
        } catch (err) {
          patch(id, { status: "error", error: errorMessage(err) });
        }
      }

      setIsPending(false);
      if (attached > 0) {
        void qc.invalidateQueries({ queryKey: ["ops", "rfq", rfqId] });
        void qc.invalidateQueries({ queryKey: ["ops", "rfqs"] });
      }
    },
    [qc, rfqId],
  );

  /** Drop the finished rows once the staffer has read them. */
  const dismiss = useCallback(() => setUploads([]), []);

  return { uploads, isPending, upload, dismiss };
}

export function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
